import { useEffect, useRef, useState } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  TextInput,
  NumberInput,
  Textarea,
  Button,
  Stack,
  Group,
  Alert,
  Divider,
  FileInput,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useNavigate } from 'react-router-dom';
import { useCreateSingleSourceForm } from '../hooks/useSingleSource';
import type { CreateSingleSourceFormDto } from '../../../api/singleSource';
import { singleSourceApi } from '../../../api/singleSource';
import { IconAlertCircle } from '@tabler/icons-react';
import { RegulatoryClausePicker } from '../../../components/RegulatoryClausePicker';

interface SingleSourceFormValues {
  formNumber: string;
  date: string;
  projectName: string;
  projectLocation: string;
  totalBudget?: number;
  amountAllocatedFromCurrentYearBudget?: number;
  totalExpenditureTillDate?: number;
  remainingBudget?: number;
  vendorName: string;
  proposedCost: number;
  proposedDuration: number;
  justification: string;
  detailsOfConditions?: string;
  decisionToSingleSource?: string;
}

export function SingleSourceForm() {
  const navigate = useNavigate();
  const createSingleSource = useCreateSingleSourceForm();
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const form = useForm<SingleSourceFormValues>({
    initialValues: {
      formNumber: '',
      date: new Date().toISOString(),
      projectName: '',
      projectLocation: '',
      totalBudget: undefined,
      amountAllocatedFromCurrentYearBudget: undefined,
      totalExpenditureTillDate: undefined,
      remainingBudget: undefined,
      vendorName: '',
      proposedCost: 0,
      proposedDuration: 0,
      justification: '',
      detailsOfConditions: '',
      decisionToSingleSource: '',
    },
    validate: {
      formNumber: (value) => (!value ? 'Form number is required' : null),
      projectName: (value) => (!value ? 'Project name is required' : null),
      projectLocation: (value) => (!value ? 'Project location is required' : null),
      vendorName: (value) => (!value ? 'Vendor name is required' : null),
      proposedCost: (value) => (value <= 0 ? 'Cost must be greater than 0' : null),
      proposedDuration: (value) =>
        value <= 0 ? 'Duration must be greater than 0' : null,
      justification: (value) => (!value ? 'Justification is required' : null),
    },
  });

  // Use refs to access form methods without causing re-renders
  const formRef = useRef(form);
  formRef.current = form;

  // Initialize Thaana keyboard for fields that need Dhivehi input
  useEffect(() => {
    const year = form.values.date
      ? new Date(form.values.date).getFullYear()
      : new Date().getFullYear();
    singleSourceApi
      .getNextFormNumber(year)
      .then(({ formNumber }) => {
        form.setFieldValue('formNumber', formNumber);
      })
      .catch(() => {
        // Non-blocking; backend will assign on create
      });
  }, [form.values.date]);

  useEffect(() => {
    // Dynamically import and initialize Thaana keyboard
    import('thaana-keyboard/ThaanaKeyboard.js').then(() => {
      if ((window as any).ThaanaKeyboard) {
        // Initialize ThaanaKeyboard only for fields with .thaana-input class
        // This ensures it only affects Dhivehi input fields, not regular fields like formNumber
        new (window as any).ThaanaKeyboard('.thaana-input');
        
        // Sync ThaanaKeyboard's DOM changes back to React form state
        // The ThaanaKeyboard directly manipulates the DOM, so we need to listen for changes
        const syncThaanaField = (inputElement: HTMLInputElement | HTMLTextAreaElement) => {
          const fieldName = inputElement.getAttribute('name');
          if (!fieldName) return;
          
          const currentForm = formRef.current;
          if (!currentForm || !currentForm.values.hasOwnProperty(fieldName)) return;
          
          const currentValue = inputElement.value;
          const formValue = currentForm.values[fieldName as keyof SingleSourceFormValues];
          
          // Only update if the value has changed to avoid infinite loops
          if (currentValue !== formValue) {
            currentForm.setFieldValue(fieldName as keyof SingleSourceFormValues, currentValue);
          }
        };
        
        // Find all actual input/textarea elements within Thaana containers
        const findThaanaInputs = (): (HTMLInputElement | HTMLTextAreaElement)[] => {
          const inputs: (HTMLInputElement | HTMLTextAreaElement)[] = [];
          const containers = document.querySelectorAll('.thaana-input');
          
          containers.forEach((container) => {
            const input = container.querySelector('input, textarea') as HTMLInputElement | HTMLTextAreaElement;
            if (input) {
              inputs.push(input);
            }
          });
          
          return inputs;
        };
        
        // Set up event listeners on all Thaana input fields
        const setupThaanaListeners = () => {
          const inputs = findThaanaInputs();
          
          inputs.forEach((input) => {
            // Add input event listener (capture phase to catch ThaanaKeyboard changes)
            const handleInput = () => {
              syncThaanaField(input);
            };
            
            // Add blur event listener to ensure sync when leaving field
            const handleBlur = () => {
              syncThaanaField(input);
            };
            
            input.addEventListener('input', handleInput, true);
            input.addEventListener('blur', handleBlur, true);
            
            // Store handlers for cleanup
            (input as any).__thaanaHandlers = { handleInput, handleBlur };
          });
        };
        
        // Use MutationObserver to watch for value changes made by ThaanaKeyboard
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'characterData' || mutation.type === 'childList') {
              const target = mutation.target as HTMLElement;
              const input = target.closest('.thaana-input')?.querySelector('input, textarea') as HTMLInputElement | HTMLTextAreaElement;
              if (input) {
                // Debounce to avoid too many updates
                clearTimeout((input as any).syncTimeout);
                (input as any).syncTimeout = setTimeout(() => {
                  syncThaanaField(input);
                }, 10);
              }
            }
          });
        });
        
        // Observe all Thaana input containers for changes
        const thaanaContainers = document.querySelectorAll('.thaana-input');
        thaanaContainers.forEach((container) => {
          observer.observe(container, {
            childList: true,
            subtree: true,
            characterData: true,
            attributes: false,
          });
        });
        
        // Set up listeners after a short delay to ensure DOM is ready
        setTimeout(setupThaanaListeners, 200);
        
        // Cleanup
        return () => {
          observer.disconnect();
          const inputs = findThaanaInputs();
          inputs.forEach((input) => {
            clearTimeout((input as any).syncTimeout);
            const handlers = (input as any).__thaanaHandlers;
            if (handlers) {
              input.removeEventListener('input', handlers.handleInput, true);
              input.removeEventListener('blur', handlers.handleBlur, true);
            }
          });
        };
      }
    }).catch((err) => {
      console.error('Failed to load Thaana keyboard:', err);
    });

    // Auto-resize textareas on mount
    const resizeTextareas = () => {
      const textareas = document.querySelectorAll('.auto-resize-textarea textarea');
      textareas.forEach((textarea) => {
        const el = textarea as HTMLTextAreaElement;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
      });
    };

    // Resize after a short delay to ensure DOM is ready
    setTimeout(resizeTextareas, 100);
  }, [form]);

  const handleFileChange = (selectedFile: File | null) => {
    setFile(selectedFile);
    setFileError(null);
    if (selectedFile) {
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ];
      if (!allowedTypes.includes(selectedFile.type)) {
        setFileError('Invalid file type. Only PDF, DOC, and DOCX files are allowed.');
      } else if (selectedFile.size > 10 * 1024 * 1024) {
        setFileError('File size exceeds 10MB limit.');
      }
    }
  };

  const handleSubmit = (values: SingleSourceFormValues) => {
    if (file && fileError) {
      return;
    }

    const submit = (payload: CreateSingleSourceFormDto) => {
      createSingleSource.mutate(payload, {
        onSuccess: (form) => {
          navigate(`/single-source/${form.id}`);
        },
      });
    };

    if (!file) {
      submit(values);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = (e.target?.result as string).split(',')[1];
      submit({
        ...values,
        fileName: file.name,
        base64File: base64,
      });
    };
    reader.readAsDataURL(file);
  };

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        <div>
          <Title order={2}>Create Single Source Procurement Form</Title>
          <Text c="dimmed" size="sm">
            The form is saved as a draft. After creating it, open the detail page and send it to
            Head of Procurement for approval.
          </Text>
        </div>

        <Card shadow="sm" p="lg">
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              {/* Form Header */}
              <Group grow>
                <TextInput
                  label="Form Number"
                  placeholder="SS-1188/2025/001"
                  required
                  readOnly
                  {...form.getInputProps('formNumber')}
                />
                <DateTimePicker
                  label="Date"
                  placeholder="Select date"
                  required
                  value={form.values.date ? new Date(form.values.date) : null}
                  onChange={(value) => form.setFieldValue('date', value?.toISOString() || '')}
                />
              </Group>

              <Divider label="Project Details" labelPosition="center" />

              <Group grow>
                <TextInput
                  label="Project Name (in Dhivehi)"
                  placeholder="Enter project name in Dhivehi"
                  required
                  className="thaana-input"
                  dir="rtl"
                  styles={{ input: { textAlign: 'right' } }}
                  name="projectName"
                  value={form.values.projectName}
                  onChange={(e) => {
                    form.setFieldValue('projectName', e.currentTarget.value);
                  }}
                  error={form.errors.projectName}
                />
                <TextInput
                  label="Project Location (in Dhivehi)"
                  placeholder="Enter project location in Dhivehi"
                  required
                  className="thaana-input"
                  dir="rtl"
                  styles={{ input: { textAlign: 'right' } }}
                  name="projectLocation"
                  value={form.values.projectLocation}
                  onChange={(e) => {
                    form.setFieldValue('projectLocation', e.currentTarget.value);
                  }}
                  error={form.errors.projectLocation}
                />
              </Group>

              <Divider label="Budget Information" labelPosition="center" />

              <Group grow>
                <NumberInput
                  label="Total Budget (MVR)"
                  placeholder="0.00"
                  min={0}
                  decimalScale={2}
                  {...form.getInputProps('totalBudget')}
                />
                <NumberInput
                  label="Amount Allocated from Current Year Budget (MVR)"
                  placeholder="0.00"
                  min={0}
                  decimalScale={2}
                  {...form.getInputProps('amountAllocatedFromCurrentYearBudget')}
                />
              </Group>

              <Group grow>
                <NumberInput
                  label="Total Expenditure Till Date (MVR)"
                  placeholder="0.00"
                  min={0}
                  decimalScale={2}
                  {...form.getInputProps('totalExpenditureTillDate')}
                />
                <NumberInput
                  label="Remaining Budget (MVR)"
                  placeholder="0.00"
                  min={0}
                  decimalScale={2}
                  {...form.getInputProps('remainingBudget')}
                />
              </Group>

              <Divider label="Vendor & Cost Information" labelPosition="center" />

              <Group grow>
                <TextInput
                  label="Vendor Name (in Dhivehi)"
                  placeholder="Enter vendor name in Dhivehi"
                  required
                  className="thaana-input"
                  dir="rtl"
                  styles={{ input: { textAlign: 'right' } }}
                  name="vendorName"
                  value={form.values.vendorName}
                  onChange={(e) => {
                    form.setFieldValue('vendorName', e.currentTarget.value);
                  }}
                  error={form.errors.vendorName}
                />
                <NumberInput
                  label="Proposed Cost (MVR)"
                  placeholder="0.00"
                  required
                  min={0}
                  decimalScale={2}
                  {...form.getInputProps('proposedCost')}
                />
              </Group>

              <NumberInput
                label="Proposed Duration (days)"
                placeholder="0"
                required
                min={1}
                {...form.getInputProps('proposedDuration')}
              />

              <Divider label="Justification & Conditions" labelPosition="center" />

              <Group justify="flex-end">
                <RegulatoryClausePicker
                  onInsert={(text) =>
                    form.setFieldValue(
                      'justification',
                      `${form.values.justification || ''}${text}`
                    )
                  }
                />
              </Group>
              <Textarea
                label="Justification (in Dhivehi)"
                placeholder="Explain why single source procurement is necessary in Dhivehi..."
                required
                minRows={4}
                className="thaana-input auto-resize-textarea paragraph-textarea"
                dir="rtl"
                styles={{ input: { textAlign: 'right', resize: 'none', overflow: 'hidden', minHeight: 'auto' } }}
                name="justification"
                value={form.values.justification}
                onChange={(e) => {
                  form.setFieldValue('justification', e.currentTarget.value);
                }}
                error={form.errors.justification}
                onInput={(e) => {
                  const textarea = e.currentTarget;
                  textarea.style.height = 'auto';
                  textarea.style.height = `${textarea.scrollHeight}px`;
                }}
              />

              <Group justify="flex-end">
                <RegulatoryClausePicker
                  onInsert={(text) =>
                    form.setFieldValue(
                      'detailsOfConditions',
                      `${form.values.detailsOfConditions || ''}${text}`
                    )
                  }
                />
              </Group>
              <Textarea
                label="Details of Conditions Leading to Single Source Procurement (in Dhivehi)"
                placeholder="Provide detailed conditions in Dhivehi..."
                minRows={3}
                className="thaana-input auto-resize-textarea paragraph-textarea"
                dir="rtl"
                styles={{ input: { textAlign: 'right', resize: 'none', overflow: 'hidden', minHeight: 'auto' } }}
                name="detailsOfConditions"
                value={form.values.detailsOfConditions || ''}
                onChange={(e) => {
                  form.setFieldValue('detailsOfConditions', e.currentTarget.value);
                }}
                error={form.errors.detailsOfConditions}
                onInput={(e) => {
                  const textarea = e.currentTarget;
                  textarea.style.height = 'auto';
                  textarea.style.height = `${textarea.scrollHeight}px`;
                }}
              />

              <Group justify="flex-end">
                <RegulatoryClausePicker
                  onInsert={(text) =>
                    form.setFieldValue(
                      'decisionToSingleSource',
                      `${form.values.decisionToSingleSource || ''}${text}`
                    )
                  }
                />
              </Group>
              <Textarea
                label="Decision to Single Source (in Dhivehi)"
                placeholder="Enter decision details in Dhivehi..."
                minRows={2}
                className="thaana-input auto-resize-textarea paragraph-textarea"
                dir="rtl"
                styles={{ input: { textAlign: 'right', resize: 'none', overflow: 'hidden', minHeight: 'auto' } }}
                name="decisionToSingleSource"
                value={form.values.decisionToSingleSource || ''}
                onChange={(e) => {
                  form.setFieldValue('decisionToSingleSource', e.currentTarget.value);
                }}
                error={form.errors.decisionToSingleSource}
                onInput={(e) => {
                  const textarea = e.currentTarget;
                  textarea.style.height = 'auto';
                  textarea.style.height = `${textarea.scrollHeight}px`;
                }}
              />

              <Divider label="Supporting Document" labelPosition="center" />

              <FileInput
                label="Upload Document (optional)"
                description="PDF, DOC, or DOCX up to 10MB"
                placeholder="Select a file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                value={file}
                onChange={handleFileChange}
                clearable
              />
              {fileError && (
                <Alert color="red" variant="light">
                  {fileError}
                </Alert>
              )}

              {createSingleSource.error && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  title="Error"
                  color="red"
                  variant="light"
                >
                  {createSingleSource.error.response?.data?.error || 'Failed to create single source form'}
                </Alert>
              )}

              <Group justify="flex-end" mt="xl">
                <Button
                  variant="light"
                  onClick={() => navigate('/single-source')}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={createSingleSource.isPending}
                >
                  Save draft
                </Button>
              </Group>
            </Stack>
          </form>
        </Card>
      </Stack>
    </Container>
  );
}
